import React, {useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {IconOpen, IconClose} from './Icons';
import './style.scss';

const DATA_VALUE = 'data-value';
const DATATYPE_OBJECT = 'object';
const CLICK_EVENT = 'click';

export function Dropdown(props) {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelection] = useState(null);
  const [allOptions, setAllOptions] = useState([]);
  let wrapRef = useRef(null);

  function externalClickEventListener(event) {
    let node = wrapRef.current;
    if (node && node.contains(event.target) === false) {
      setIsOpen(false);
    }
  }

  function setSelectionFromList(list, value) {
    let option = list.find((option) => option.value === value);
    if (isValid(option)) {
      setSelection(option);
      return option;
    }
  }

  function isValid(value) {
    return value != undefined && value != null;
  }

  function makeOptionList(optionsList) {
    return optionsList.map((option) => {
      if (typeof option === DATATYPE_OBJECT) {
        return {...option};
      } else {
        return {value: option, label: option};
      }
    });
  }

  useEffect(() => {
    document.addEventListener(CLICK_EVENT, externalClickEventListener);
    return () => {
      document.removeEventListener(CLICK_EVENT, externalClickEventListener);
    };
  }, []);

  useEffect(() => {
    let toSetOptions = makeOptionList(props.options);
    setAllOptions(toSetOptions);
    if (selected === null && isValid(props.defaultValue)) {
      setSelectionFromList(toSetOptions, props.defaultValue);
    } else if (isValid(props.value)) {
      setSelectionFromList(toSetOptions, props.value);
    }
  }, [props.options, props.value]);

  function selectedOption() {
    if (selected === null) {
      return props.placeholder;
    } else {
      return selected.label;
    }
  }

  function getIcon() {
    let icon = props.iconOpen;
    if (isOpen) icon = props.iconClose;
    return React.createElement(icon, {className: 'icon'});
  }

  function openList(event) {
    event.preventDefault();
    event.stopPropagation();
    if (props.disabled) {
      setIsOpen(false);
    } else {
      setIsOpen(!isOpen);
    }
  }

  function optionClick(event) {
    event.preventDefault();
    event.stopPropagation();
    let value = parseInt(event.target.getAttribute(DATA_VALUE));
    let option = setSelectionFromList(allOptions, value);
    setIsOpen(false);
    option && props.onChange(option);
  }

  function setOptionClassName({value}) {
    return selected && value === selected.value
      ? 'option-item selected'
      : 'option-item';
  }

  function renderOptions() {
    return allOptions.map((option) => (
      <li
        key={option.value}
        className={setOptionClassName(option)}
        data-value={option.value}
        onClick={optionClick}>
        {option.label}
      </li>
    ));
  }

  function getHeaderClassName() {
    return `react-dropdown-header ${props.disabled ? 'disabled' : ''}`;
  }

  function getWrapperClassName() {
    return `react-dropdown ${props.className}`;
  }

  function renderOptionsList() {
    return (
      isOpen && <ul className='react-dropdown-options'>{renderOptions()}</ul>
    );
  }

  return (
    <div className={getWrapperClassName()} ref={wrapRef}>
      <div className={getHeaderClassName()} onClick={openList}>
        <span className='label'>{selectedOption()}</span>
        {getIcon()}
      </div>
      {renderOptionsList()}
    </div>
  );
}

Dropdown.propTypes = {
  /**
   * Default selected option in list
   */
  defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Placeholder message to be displayed on the dropdown
   */
  placeholder: PropTypes.string,
  /**
   * List of options to be displayed in dropdown
   */
  options: PropTypes.array.isRequired,
  /**
   * Callback function to  be executed when a new option is selected.
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Currently selected option.
   */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Boolean value specifying if dropdown is disabled
   */
  disabled: PropTypes.bool,
  /**
   * String value to be set as className
   */
  className: PropTypes.string,
};

Dropdown.defaultProps = {
  placeholder: 'Please select.',
  className: '',
  value: undefined,
  defaultValue: undefined,
  iconClose: IconClose,
  iconOpen: IconOpen,
};
